import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import {
  callQueue,
  createAdminAccount,
  deleteAdminAccount,
  getAdminAccounts,
  getAdminQueues,
  getPublicConfig,
  recallQueue,
  retryNotification,
  updateQueueStatus,
} from "../api";
import { getSession, signIn, signOut, type AuthSession } from "../auth";
import { CALL_PURPOSES, callPurposeLabel } from "../callPurpose";
import { Layout } from "../components/Layout";
import { PatientHistoryPanel } from "../components/PatientHistoryPanel";
import { QrImage } from "../components/QrImage";
import { StatusBadge } from "../components/StatusBadge";
import type { AdminAccount, AdminQueue, PublicConfig } from "../types";

type Panel = "queue" | "patient-history" | "booking-qr" | "admin-accounts";
type Theme = "light" | "dark";

const THEME_STORAGE_KEY = "medinotify-admin-theme";

function initialTheme(): Theme {
  try {
    const savedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (savedTheme === "light" || savedTheme === "dark") return savedTheme;
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  } catch {
    return "light";
  }
}

function ThemeSwitch({ theme, onToggle }: { theme: Theme; onToggle: () => void }) {
  return (
    <button
      type="button"
      className="theme-switch"
      role="switch"
      aria-checked={theme === "dark"}
      aria-label={theme === "dark" ? "ปิดธีมมืด" : "เปิดธีมมืด"}
      onClick={onToggle}
    >
      <span className="theme-switch__container" aria-hidden="true">
        <span className="theme-switch__circle-container">
          <span className="theme-switch__sun-moon-container">
            <span className="theme-switch__moon">
              <span className="theme-switch__spot" />
              <span className="theme-switch__spot" />
              <span className="theme-switch__spot" />
            </span>
          </span>
        </span>
        <span className="theme-switch__clouds" />
        <span className="theme-switch__stars-container">✦ · ✧</span>
      </span>
      <span className="theme-switch__label">ธีมมืด</span>
    </button>
  );
}

export function AdminPage() {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [queues, setQueues] = useState<AdminQueue[]>([]);
  const [panel, setPanel] = useState<Panel>("queue");
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [busy, setBusy] = useState("");
  const [toast, setToast] = useState("");
  const [toastError, setToastError] = useState(false);
  const [accounts, setAccounts] = useState<AdminAccount[]>([]);
  const [currentAdminUserId, setCurrentAdminUserId] = useState("");
  const [accountEmail, setAccountEmail] = useState("");
  const [accountPassword, setAccountPassword] = useState("");
  const [accountDisplayName, setAccountDisplayName] = useState("");
  const [theme, setTheme] = useState<Theme>(initialTheme);

  useLayoutEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // The selected theme still works for this session when storage is unavailable.
    }
  }, [theme]);

  const refresh = useCallback(async () => {
    const data = await getAdminQueues();
    setQueues(data);
  }, []);

  useEffect(() => {
    void getSession().then(setSession);
  }, []);

  useEffect(() => {
    if (!session) return;
    void Promise.all([refresh(), getPublicConfig().then(setConfig)]).catch((error) => {
      setToast(error instanceof Error ? error.message : "โหลดข้อมูลไม่สำเร็จ");
    });
    const timer = window.setInterval(() => void refresh().catch(() => undefined), 15_000);
    return () => window.clearInterval(timer);
  }, [refresh, session]);

  const stats = useMemo(
    () => ({
      waiting: queues.filter((queue) => queue.status === "WAITING").length,
      called: queues.filter((queue) => queue.status === "CALLED").length,
      completed: queues.filter((queue) => queue.status === "COMPLETED").length,
    }),
    [queues],
  );

  async function handleLogin(event: React.FormEvent) {
    event.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      setSession(await signIn(email, password));
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : "เข้าสู่ระบบไม่สำเร็จ");
    }
    setLoggingIn(false);
  }

  async function runAction(label: string, action: () => Promise<unknown>) {
    setBusy(label);
    setToast("");
    setToastError(false);
    try {
      await action();
      await refresh();
      setToast("บันทึกสำเร็จ");
    } catch (error) {
      setToastError(true);
      setToast(error instanceof Error ? error.message : "ดำเนินการไม่สำเร็จ");
    } finally {
      setBusy("");
    }
  }

  async function loadAccounts() {
    setBusy("load-accounts");
    setToast("");
    setToastError(false);
    try {
      const data = await getAdminAccounts();
      setAccounts(data.accounts);
      setCurrentAdminUserId(data.currentUserId);
    } catch (error) {
      setToastError(true);
      setToast(error instanceof Error ? error.message : "โหลดบัญชี Admin ไม่สำเร็จ");
    } finally {
      setBusy("");
    }
  }

  async function handleCreateAccount(event: React.FormEvent) {
    event.preventDefault();
    setBusy("create-account");
    setToast("");
    setToastError(false);
    try {
      await createAdminAccount({
        email: accountEmail,
        password: accountPassword,
        displayName: accountDisplayName,
      });
      setAccountEmail("");
      setAccountPassword("");
      setAccountDisplayName("");
      const data = await getAdminAccounts();
      setAccounts(data.accounts);
      setCurrentAdminUserId(data.currentUserId);
      setToast("เพิ่มบัญชี Admin สำเร็จ");
    } catch (error) {
      setToastError(true);
      setToast(error instanceof Error ? error.message : "เพิ่มบัญชี Admin ไม่สำเร็จ");
    } finally {
      setBusy("");
    }
  }

  async function handleDeleteAccount(account: AdminAccount) {
    const confirmed = window.confirm(
      `ยืนยันลบบัญชี ${account.displayName} (${account.email})?\nบัญชีนี้จะเข้าสู่ระบบไม่ได้อีก`,
    );
    if (!confirmed) return;
    setBusy(`delete-account:${account.userId}`);
    setToast("");
    setToastError(false);
    try {
      await deleteAdminAccount(account.userId);
      setAccounts((current) => current.filter((item) => item.userId !== account.userId));
      setToast("ลบบัญชี Admin สำเร็จ");
    } catch (error) {
      setToastError(true);
      setToast(error instanceof Error ? error.message : "ลบบัญชี Admin ไม่สำเร็จ");
    } finally {
      setBusy("");
    }
  }

  function switchPanel(nextPanel: Panel) {
    setPanel(nextPanel);
    setToast("");
    setToastError(false);
    if (nextPanel === "admin-accounts") void loadAccounts();
  }

  if (!session) {
    return (
      <Layout compact>
        <section className="admin-login">
          <div className="admin-login__header">
            <div className="admin-login__identity">
              <span className="lock-mark">••</span>
              <div>
                <small>STAFF ACCESS</small>
                <h1>เข้าสู่ระบบเจ้าหน้าที่</h1>
              </div>
            </div>
            <ThemeSwitch
              theme={theme}
              onToggle={() => setTheme((current) => current === "dark" ? "light" : "dark")}
            />
          </div>
          <form onSubmit={handleLogin}>
            <label>
              ชื่อผู้ใช้ (อีเมล)
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="username"
                placeholder="staff@example.com"
                required
              />
            </label>
            <label>
              รหัสผ่าน
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                placeholder="อย่างน้อย 12 ตัวอักษร"
                required
              />
            </label>
            {loginError && <p className="form-error">{loginError}</p>}
            <button className="primary-button" disabled={loggingIn}>
              {loggingIn ? "กำลังตรวจสอบ…" : "เข้าสู่ระบบ"}
            </button>
          </form>
          <p className="privacy-note">บัญชีเจ้าหน้าที่สร้างผ่าน Supabase Auth เท่านั้น</p>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="admin-heading">
        <div>
          <div className="eyebrow">STAFF CONTROL</div>
          <h1>จัดการคิววันนี้</h1>
          <p>{new Intl.DateTimeFormat("th-TH", { dateStyle: "full" }).format(new Date())}</p>
        </div>
        <div className="admin-heading__actions">
          <ThemeSwitch
            theme={theme}
            onToggle={() => setTheme((current) => current === "dark" ? "light" : "dark")}
          />
          <button
            type="button"
            className="logout-button"
            onClick={() => void signOut().then(() => setSession(null))}
          >
            ออกจากระบบ
            <span aria-hidden="true" />
          </button>
        </div>
      </section>

      <section className="stat-grid">
        <div><small>กำลังรอ</small><strong>{stats.waiting}</strong><span>คิว</span></div>
        <div><small>กำลังเรียก</small><strong>{stats.called}</strong><span>คิว</span></div>
        <div><small>เสร็จแล้ว</small><strong>{stats.completed}</strong><span>คิว</span></div>
      </section>

      <nav className="admin-tabs" aria-label="เมนูเจ้าหน้าที่">
        <button className={panel === "queue" ? "active" : ""} onClick={() => switchPanel("queue")}>รายการคิว</button>
        <button className={panel === "patient-history" ? "active" : ""} onClick={() => switchPanel("patient-history")}>ค้นหาและประวัติผู้ป่วย</button>
        <button className={panel === "booking-qr" ? "active" : ""} onClick={() => switchPanel("booking-qr")}>QR จองคิว</button>
        <button className={panel === "admin-accounts" ? "active" : ""} onClick={() => switchPanel("admin-accounts")}>จัดการบัญชี Admin</button>
      </nav>

      {toast && <div className={`toast${toastError ? " toast--error" : ""}`} role="status">{toast}</div>}

      {panel === "queue" && (
        <section className="queue-panel">
          <div className="panel-toolbar">
            <div>
              <h2>คิวทั้งหมด</h2>
              <p>อัปเดตอัตโนมัติทุก 15 วินาที</p>
            </div>
          </div>

          <div className="queue-list">
            {queues.length === 0 ? (
              <div className="empty-list">
                <b>ยังไม่มีคิววันนี้</b>
                <span>เมื่อมีลูกค้าจอง รายการจะแสดงที่นี่</span>
              </div>
            ) : (
              queues.map((queue) => (
                <article className="queue-row" key={queue.id}>
                  <div className="queue-row__number">{queue.queueNumber}</div>
                  <div className="queue-row__person">
                    <strong>{queue.customerName}</strong>
                    <span>
                      {queue.customerPhoneNumber ?? "ไม่มีเบอร์โทร"}
                      {" · "}
                      {new Intl.DateTimeFormat("th-TH", {
                        hour: "2-digit",
                        minute: "2-digit",
                      }).format(new Date(queue.createdAt))}
                    </span>
                    {queue.callPurpose && (
                      <span className="call-purpose">
                        เรียก: {callPurposeLabel(queue.callPurpose)}
                      </span>
                    )}
                  </div>
                  <StatusBadge status={queue.status} />
                  {queue.notificationStatus === "FAILED" && (
                    <span className="notify-failed">LINE ส่งไม่สำเร็จ</span>
                  )}
                  <div className="queue-actions">
                    {queue.status === "WAITING" && (
                      <div className="call-purpose-actions">
                        {CALL_PURPOSES.map((purpose) => (
                          <button
                            key={purpose.value}
                            disabled={busy !== ""}
                            onClick={() => void runAction(
                              `${queue.id}:${purpose.value}`,
                              () => callQueue(queue.id, purpose.value),
                            )}
                          >
                            {purpose.label}
                          </button>
                        ))}
                      </div>
                    )}
                    {queue.status === "CALLED" && (
                      <>
                        <button disabled={busy !== ""} onClick={() => void runAction(queue.id, () => recallQueue(queue.id))}>เรียกซ้ำ</button>
                        <button disabled={busy !== ""} className="success" onClick={() => void runAction(queue.id, () => updateQueueStatus(queue.id, "COMPLETED"))}>เสร็จ</button>
                        <button disabled={busy !== ""} onClick={() => void runAction(queue.id, () => updateQueueStatus(queue.id, "NO_SHOW"))}>ไม่มา</button>
                      </>
                    )}
                    {queue.notificationStatus === "FAILED" && (
                      <button disabled={busy !== ""} onClick={() => void runAction(queue.id, () => retryNotification(queue.id))}>ส่ง LINE ซ้ำ</button>
                    )}
                    {(queue.status === "WAITING" || queue.status === "CALLED") && (
                      <button disabled={busy !== ""} className="danger" onClick={() => void runAction(queue.id, () => updateQueueStatus(queue.id, "CANCELLED"))}>ยกเลิก</button>
                    )}
                  </div>
                </article>
              ))
            )}
          </div>
        </section>
      )}

      {panel === "booking-qr" && (
        <section className="booking-qr-panel">
          <div>
            <small>BOOKING POINT</small>
            <h2>Static QR สำหรับจองคิว</h2>
            <p>QR นี้ใช้ URL เดิมตลอด พิมพ์ติดหน้าร้านได้ ลูกค้าจะเปิดหน้าจองผ่าน LINE LIFF</p>
            {config && (
              <a className="secondary-button" href={config.bookingUrl} target="_blank" rel="noreferrer">
                ทดลองเปิดหน้าจอง
              </a>
            )}
          </div>
          {config ? (
            <div className="master-qr">
              <QrImage
                value={config.bookingUrl}
                label="QR สำหรับจองคิว"
                size={280}
                downloadName="static-booking-qr.png"
              />
              <strong>สแกนเพื่อรับบัตรคิว</strong>
              <span>{config.appName}</span>
            </div>
          ) : (
            <div className="qr-placeholder" />
          )}
        </section>
      )}

      {panel === "patient-history" && <PatientHistoryPanel />}

      {panel === "admin-accounts" && (
        <section className="admin-accounts-panel">
          <div className="account-form-block">
            <small>ACCOUNT CONTROL</small>
            <h2>เพิ่มบัญชี Admin</h2>
            <p>บัญชีที่สร้างจะยืนยันอีเมลอัตโนมัติและสามารถเข้าสู่หน้าเจ้าหน้าที่ได้ทันที</p>
            <form onSubmit={handleCreateAccount}>
              <label>
                ชื่อที่แสดง
                <input
                  value={accountDisplayName}
                  onChange={(event) => setAccountDisplayName(event.target.value)}
                  minLength={2}
                  maxLength={100}
                  autoComplete="off"
                  placeholder="เช่น เจ้าหน้าที่เวรเช้า"
                  required
                />
              </label>
              <label>
                อีเมลสำหรับเข้าสู่ระบบ
                <input
                  type="email"
                  value={accountEmail}
                  onChange={(event) => setAccountEmail(event.target.value)}
                  autoComplete="off"
                  placeholder="staff@example.com"
                  required
                />
              </label>
              <label>
                รหัสผ่านเริ่มต้น
                <input
                  type="password"
                  value={accountPassword}
                  onChange={(event) => setAccountPassword(event.target.value)}
                  minLength={12}
                  maxLength={128}
                  autoComplete="new-password"
                  placeholder="อย่างน้อย 12 ตัวอักษร"
                  required
                />
              </label>
              <button className="primary-button primary-button--small" disabled={busy !== ""}>
                {busy === "create-account" ? "กำลังเพิ่มบัญชี…" : "เพิ่มบัญชี Admin"}
              </button>
            </form>
          </div>

          <div className="account-list-block">
            <div className="panel-toolbar">
              <div>
                <h2>บัญชี Admin ทั้งหมด</h2>
                <p>ไม่สามารถลบบัญชีที่กำลังใช้งานหรือ Admin คนสุดท้ายได้</p>
              </div>
              <button className="secondary-button" disabled={busy !== ""} onClick={() => void loadAccounts()}>
                โหลดใหม่
              </button>
            </div>
            <div className="account-list">
              {busy === "load-accounts" && accounts.length === 0 ? (
                <div className="empty-list"><b>กำลังโหลดบัญชี…</b></div>
              ) : accounts.length === 0 ? (
                <div className="empty-list"><b>ไม่พบบัญชี หรือบัญชีนี้ไม่มีสิทธิ์จัดการ</b></div>
              ) : (
                accounts.map((account) => (
                  <article className="account-row" key={account.userId}>
                    <div className="account-avatar">{account.displayName.slice(0, 1).toUpperCase()}</div>
                    <div>
                      <strong>{account.displayName}</strong>
                      <span>{account.email}</span>
                      <small>
                        {account.role}
                        {account.userId === currentAdminUserId ? " · บัญชีที่กำลังใช้" : ""}
                      </small>
                    </div>
                    <button
                      className="danger-button"
                      disabled={busy !== "" || account.userId === currentAdminUserId}
                      onClick={() => void handleDeleteAccount(account)}
                    >
                      {busy === `delete-account:${account.userId}` ? "กำลังลบ…" : "ลบบัญชี"}
                    </button>
                  </article>
                ))
              )}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}
